import { useRef } from 'react'

/**
 * Default identity function for HappyTask entities — every table row is a
 * `BaseEntity` with `id` + `updatedAt`, so `${id}|${updatedAt}` uniquely
 * captures identity and content version in one string.
 *
 * Callers that want to stabilize by a different field (e.g. a derived
 * rendering key) can pass their own function to `useStableEntityArray`.
 */
export function baseEntityIdentity(entity: { id: string; updatedAt?: Date | null }): string {
  return `${entity.id}|${entity.updatedAt?.getTime() ?? 0}`
}


/**
 * Like {@link useStableLiveQuery}, but stabilizes references at the
 * **entity level** (not just the array level). Dexie returns fresh object
 * instances on every subscription firing — even when no field changed —
 * so a strict `Object.is` comparison defeats every downstream `React.memo`
 * and `useMemo`. The array-level wrapper helps when every single element
 * happens to be `Object.is`-equal (rarely true for Dexie output), but
 * fails whenever any element is a new object.
 *
 * This hook rebuilds the array by identity: for each fresh entity, if the
 * previous run had an entity with the same identity-key and same
 * version-token (e.g. `updatedAt`), the previous reference is kept. Only
 * genuinely-changed entities get new references.
 *
 * The returned array itself is also kept stable when the sequence of
 * entities (identity + version) is unchanged — so `useMemo` that depends
 * on it bails.
 *
 * @example
 * // Stabilize a Dexie task list so re-subscriptions don't force a full
 * // render when nothing in the database actually changed.
 * const stable = useStableEntityArray(
 *   tasksFromDexie,
 *   t => `${t.id}|${t.updatedAt?.getTime() ?? 0}`,
 * )
 *
 * @param value     The fresh array (may be `undefined` while loading).
 * @param identify  Returns a string that captures the entity's identity
 *                  AND its current content version (typically
 *                  `${id}|${updatedAt.getTime()}`). Two renders with the
 *                  same key for the same entity mean "same entity, same
 *                  content" and the previous reference is reused.
 */
export function useStableEntityArray<T>(
  value: T[] | undefined,
  identify: (item: T) => string,
): T[] | undefined {
  const prevArrayRef = useRef<T[] | undefined>(undefined)
  const prevByKeyRef = useRef<Map<string, T> | undefined>(undefined)

  // Transient `undefined` (loading state) → keep the last known array so
  // downstream memos don't thrash between defined/undefined.
  if (value === undefined) return prevArrayRef.current

  const prevArray = prevArrayRef.current
  if (prevArray === undefined) {
    // First run with data — return the caller's array as-is and seed the
    // per-key cache for future comparisons.
    const byKey = new Map<string, T>()
    for (const item of value) byKey.set(identify(item), item)
    prevArrayRef.current = value
    prevByKeyRef.current = byKey
    return value
  }

  const prevByKey = prevByKeyRef.current!
  const stabilized: T[] = new Array(value.length)
  let anyUnmatched = false
  for (let i = 0; i < value.length; i++) {
    const fresh = value[i]
    const key = identify(fresh)
    const prev = prevByKey.get(key)
    if (prev !== undefined) {
      stabilized[i] = prev
    } else {
      stabilized[i] = fresh
      anyUnmatched = true
    }
  }

  // If the sequence (length + per-index identity) is unchanged, return
  // the previous array reference too — this is the common steady-state
  // path and is what lets downstream `useMemo([...deps, tasks])` short-
  // circuit without any work.
  if (
    !anyUnmatched &&
    prevArray.length === stabilized.length &&
    prevArray.every((item, i) => item === stabilized[i])
  ) {
    return prevArray
  }

  const nextByKey = new Map<string, T>()
  for (const item of stabilized) nextByKey.set(identify(item), item)
  prevArrayRef.current = stabilized
  prevByKeyRef.current = nextByKey
  return stabilized
}
